import React, { useEffect, createElement } from 'react';
const SITE_NAME = 'KittyPot';
const SITE_URL = 'https://www.kittypot.com';
const SOCIAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/iFCdmKPA1edU6x7RzhLhxy/WhatsApp_Image_2026-06-24_at_00.08.47.jpg';
type SEOProps = {
  /** Full document title, e.g. "KittyPot | No More Chasing Friends for Money" */
  title: string;
  /** Meta description used for search results */
  description: string;
  /** Open Graph / Twitter title. Falls back to `title`. */
  ogTitle?: string;
  /** Open Graph / Twitter description. Falls back to `description`. */
  ogDescription?: string;
  /** Path for canonical + og:url, e.g. "/faq" */
  path?: string;
  /** Optional JSON-LD structured data object */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};
/** Upsert a <meta> tag by name or property attribute. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
/**
 * SEO/head manager. Because the project's static index.html head can't be
 * edited directly, this component keeps the document title, meta description,
 * Open Graph, Twitter card and structured data in sync with the current page.
 */
export function SEO({
  title,
  description,
  ogTitle,
  ogDescription,
  path = '/',
  jsonLd
}: SEOProps) {
  const resolvedOgTitle = ogTitle ?? title;
  const resolvedOgDescription = ogDescription ?? description;
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    // Open Graph
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', resolvedOgTitle);
    upsertMeta('property', 'og:description', resolvedOgDescription);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', SOCIAL_IMAGE);
    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', resolvedOgTitle);
    upsertMeta('name', 'twitter:description', resolvedOgDescription);
    upsertMeta('name', 'twitter:image', SOCIAL_IMAGE);
    upsertLink('canonical', url);
    // Structured data
    const SCRIPT_ID = 'mp-structured-data';
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = SCRIPT_ID;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, resolvedOgTitle, resolvedOgDescription, url, jsonLd]);
  return null;
}